import { gql } from '@apollo/client';

/**
 * Trash can — soft-deleted Assets / Playlists / Schedules / Folders.
 *
 * Items reach Trash via `DELETE_OBJECTS` with `payload.isTrash: true`. Every operation below is
 * gated server-side to OWNER + SUPER_ADMIN root roles.
 *
 * `type` is the same value the page already sends as `DeleteObjectInput.type`
 * ('ASSET' / 'PLAYLIST' / 'SCHEDULE').
 *
 * Types are hand-written (this package has no codegen) and mirror the open-api schema.
 */

// --- Fragments ---

export const TRASH_ITEM_FIELDS = gql`
  fragment TrashItemFields on TrashItem {
    _id
    objectType
    groupType
    isFolder
    name
    path
    trashRootId
    deletedBy
    deletedByName
    deletedAt
    purgeAt
    purgeState
    doc
  }
`;

// --- Queries ---

/**
 * List trashed items for a page type.
 * `parentId` absent → Trash root rows; set → direct children of that trashed folder.
 */
export const TRASH_ITEMS = gql`
  query TrashItems(
    $teamId: String
    $type: OBJECT_TYPES!
    $parentId: ID
    $offset: Int
    $limit: Int
    $search: String
    $sortField: String
    $sortDirection: String
  ) {
    trashItems(
      teamId: $teamId
      type: $type
      parentId: $parentId
      offset: $offset
      limit: $limit
      search: $search
      sortField: $sortField
      sortDirection: $sortDirection
    ) {
      total
      items {
        ...TrashItemFields
      }
    }
  }
  ${TRASH_ITEM_FIELDS}
`;

/** Counts for the Trash header progress ("Emptying trash… N of M"). Poll while `deleting > 0`. */
export const TRASH_STATUS = gql`
  query TrashStatus($teamId: String, $type: OBJECT_TYPES!) {
    trashStatus(teamId: $teamId, type: $type) {
      total
      deleting
    }
  }
`;

// --- Mutations ---

/** Restore rows to the location they were deleted from (Trash root rows only). */
export const RESTORE_TRASH_ITEMS = gql`
  mutation RestoreTrashItems($teamId: String, $ids: [ID!]!, $skipPlaylistsOverLimit: Boolean) {
    restoreTrashItems(teamId: $teamId, ids: $ids, skipPlaylistsOverLimit: $skipPlaylistsOverLimit) {
      restored
      failed {
        id
        reason
        details
      }
    }
  }
`;

/** Move rows out of Trash into a chosen folder — `targetPath: null` is Root, as everywhere else. */
export const MOVE_TRASH_ITEMS = gql`
  mutation MoveTrashItems($teamId: String, $ids: [ID!]!, $targetPath: String) {
    moveTrashItems(teamId: $teamId, ids: $ids, targetPath: $targetPath) {
      restored
      failed {
        id
        reason
        details
      }
    }
  }
`;

/** Delete permanently — queued, purged in the background. */
export const PURGE_TRASH_ITEMS = gql`
  mutation PurgeTrashItems($teamId: String, $ids: [ID!]!) {
    purgeTrashItems(teamId: $teamId, ids: $ids) {
      queued
    }
  }
`;

/** Empty trash for one page type — queued, purged in the background. */
export const EMPTY_TRASH = gql`
  mutation EmptyTrash($teamId: String, $type: OBJECT_TYPES!) {
    emptyTrash(teamId: $teamId, type: $type) {
      queued
    }
  }
`;

// --- Types ---

/** `purgeState` is null, or 'deleting' while a Delete permanently / Empty trash purge runs. */
export type TrashPurgeState = 'deleting';

export interface TrashItem {
  _id: string;
  /** OBJECT_TYPES value of the stored doc (asset / playlist / schedule / group). */
  objectType: string;
  /** For folder rows: which page the folder belongs to. */
  groupType?: string | null;
  isFolder: boolean;
  name?: string | null;
  /** Original location. */
  path?: string | null;
  trashRootId?: string | null;
  deletedBy?: string | null;
  deletedByName?: string | null;
  deletedAt?: string | null;
  purgeAt?: string | null;
  purgeState?: TrashPurgeState | null;
  /** The trashed document as it was stored — rendered with the same components as a live item. */
  doc?: Record<string, unknown> | null;
}

export interface TrashItemList {
  total: number;
  items: TrashItem[];
}

export interface TrashItemsQueryResult {
  trashItems: TrashItemList;
}

export interface TrashItemsQueryVariables {
  teamId?: string;
  type: string;
  parentId?: string | null;
  offset?: number;
  limit?: number;
  /** Case-insensitive substring of the displayed name. */
  search?: string;
  /** name | deletedBy | deletedAt | purgeAt — anything else means newest deleted first. */
  sortField?: string;
  /** 'ASC' / 'DESC' (the portal's `SortDirection`); the backend lower-cases before comparing. */
  sortDirection?: string;
}

export interface TrashStatus {
  total: number;
  deleting: number;
}

export interface TrashStatusQueryResult {
  trashStatus: TrashStatus;
}

export interface TrashStatusQueryVariables {
  teamId?: string;
  type: string;
}

export interface TrashFailure {
  id: string;
  reason: string;
  /**
   * Reason-specific context, when the server has any. For the Free Plan restore refusals
   * (#2626) these are the NAMES of every playlist that would go over its limit — the dialog
   * both tells the user where to remove items and offers to restore the asset without those
   * playlists, which has to name what it would be dropped from. Nullable on the wire.
   */
  details?: string[] | null;
}

export interface TrashRestoreResult {
  restored: number;
  failed: TrashFailure[];
}

export interface RestoreTrashItemsResult {
  restoreTrashItems: TrashRestoreResult;
}

export interface RestoreTrashItemsVariables {
  teamId?: string;
  ids: string[];
  /**
   * Free Plan (#2626): bring the document back WITHOUT the playlists that are at their limit,
   * instead of refusing the restore. The user's answer to the refusal dialog, never a default —
   * dropping a row edits a playlist that may be on a screen right now.
   */
  skipPlaylistsOverLimit?: boolean;
}

export interface MoveTrashItemsResult {
  moveTrashItems: TrashRestoreResult;
}

export interface MoveTrashItemsVariables {
  teamId?: string;
  ids: string[];
  /** Destination folder path; `null` is Root, the same shape every other surface uses. */
  targetPath: string | null;
}

export interface TrashQueueResult {
  queued: number;
}

export interface PurgeTrashItemsResult {
  purgeTrashItems: TrashQueueResult;
}

export interface PurgeTrashItemsVariables {
  teamId?: string;
  ids: string[];
}

export interface EmptyTrashResult {
  emptyTrash: TrashQueueResult;
}

export interface EmptyTrashVariables {
  teamId?: string;
  type: string;
}
